import { useEffect, useRef, useState } from 'react';

import { Loader2, Palette, RotateCcw, Save, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  useServiceHistorySettings,
  useServiceVehicles,
  useUpdateServiceHistorySettings,
} from '@/hooks/useServiceHistory';
import { serviceHistoryApi } from '@/lib/api/service-history.api';

import { DEFAULT_SERVICE_PDF_THEME, servicePdfThemeSchema } from '@repo/shared-types';

import type { ServicePdfAssetKind, ServicePdfTheme } from '@repo/shared-types';

const SAMPLE = '__sample__';
const PREVIEW_DELAY_MS = 700;

const FONT_LABELS: Record<ServicePdfTheme['font'], string> = {
  Helvetica: 'Helvetica (clean, modern)',
  'Times-Roman': 'Times (classic, serif)',
  Courier: 'Courier (typewriter)',
  custom: 'Custom (upload your brand font)',
};

const HEADER_LABELS: Record<ServicePdfTheme['header_style'], string> = {
  banner: 'Banner: full-width colour bar with logo',
  line: 'Line: logo and a coloured rule',
  minimal: 'Minimal: logo and title only',
};

const COLOR_FIELDS: Array<{ key: keyof ServicePdfTheme; label: string }> = [
  { key: 'primary_color', label: 'Primary (banner, headings)' },
  { key: 'accent_color', label: 'Accent (lines, summary bar)' },
  { key: 'text_color', label: 'Text' },
  { key: 'table_header_color', label: 'Table header' },
];

const sameTheme = (a: ServicePdfTheme, b: ServicePdfTheme) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Branding for the customer-facing PDF, with a live preview of the real PDF.
 * One design per platform; it applies to every PDF built after saving.
 */
export const ServicePdfDesignCard = () => {
  const { data: settings } = useServiceHistorySettings();
  const { data: vehicles } = useServiceVehicles({ page: 1, limit: 50 });
  const updateSettings = useUpdateServiceHistorySettings();
  const { toast } = useToast();

  // null = unedited, so the form always follows what was last saved.
  const [edited, setEdited] = useState<ServicePdfTheme | null>(null);
  const theme = edited ?? settings?.pdf_theme ?? DEFAULT_SERVICE_PDF_THEME;
  const setTheme = (update: (current: ServicePdfTheme) => ServicePdfTheme) =>
    setEdited((current) => update(current ?? settings?.pdf_theme ?? DEFAULT_SERVICE_PDF_THEME));
  const [previewCar, setPreviewCar] = useState(SAMPLE);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState<ServicePdfAssetKind | null>(null);
  const urlRef = useRef<string | null>(null);

  // Half-typed values (e.g. a colour) aren't sent, so the preview never shows errors mid-edit.
  const validation = servicePdfThemeSchema.safeParse(theme);
  const invalidMessage = validation.success ? null : validation.error.issues[0]?.message;

  // Re-render the preview shortly after each change.
  useEffect(() => {
    if (!servicePdfThemeSchema.safeParse(theme).success) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const blob = await serviceHistoryApi.previewPdf(
          theme,
          previewCar === SAMPLE ? undefined : previewCar,
        );
        if (cancelled) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(blob);
        setPreviewUrl(urlRef.current);
        setPreviewError(null);
      } catch {
        if (!cancelled) setPreviewError('Preview could not be built. Check the fields above.');
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [theme, previewCar]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const set = <K extends keyof ServicePdfTheme>(key: K, value: ServicePdfTheme[K]) =>
    setTheme((current) => ({ ...current, [key]: value }));

  const upload = async (kind: ServicePdfAssetKind, file: File | undefined) => {
    if (!file) return;
    setUploading(kind);
    try {
      const path = await serviceHistoryApi.uploadThemeAsset(kind, file);
      const key = kind === 'logo' ? 'logo_path' : `${kind}_path`;
      setTheme((current) => ({ ...current, [key]: path }));
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async () => {
    await updateSettings.mutateAsync({ pdf_theme: theme });
    setEdited(null);
    toast({ title: 'PDF design saved', description: 'New PDFs use it from now on.' });
  };

  const changed = !!settings && !sameTheme(theme, settings.pdf_theme);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Palette className="text-muted-foreground h-5 w-5" />
          <CardTitle className="text-sm">PDF design</CardTitle>
        </div>
        <CardDescription>
          How the service history PDF sent to customers looks. The preview on the right is the real
          PDF and updates as you edit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Brand name">
                <Input
                  value={theme.brand_name}
                  onChange={(e) => set('brand_name', e.target.value)}
                  placeholder="V12 Sports & Classics"
                />
              </Field>
              <Field label="Title">
                <Input value={theme.title} onChange={(e) => set('title', e.target.value)} />
              </Field>
            </div>
            <Field label="Subtitle (under the registration)">
              <Input
                value={theme.subtitle}
                onChange={(e) => set('subtitle', e.target.value)}
                placeholder="Certified service record"
              />
            </Field>

            <Field label="Logo (PNG or JPG, up to 2 MB)">
              <AssetPicker
                accept=".png,.jpg,.jpeg"
                path={theme.logo_path}
                busy={uploading === 'logo'}
                onPick={(file) => upload('logo', file)}
                onClear={() => set('logo_path', null)}
              />
            </Field>

            <Field label="Header style">
              <Select
                value={theme.header_style}
                onValueChange={(value) =>
                  set('header_style', value as ServicePdfTheme['header_style'])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(HEADER_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              {COLOR_FIELDS.map(({ key, label }) => (
                <Field key={key} label={label}>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme[key] as string}
                      onChange={(e) => set(key, e.target.value as never)}
                      className="h-9 w-12 cursor-pointer rounded border"
                      aria-label={label}
                    />
                    <Input
                      value={theme[key] as string}
                      onChange={(e) => set(key, e.target.value as never)}
                      className="font-mono text-xs"
                    />
                  </div>
                </Field>
              ))}
            </div>

            <Field label="Font">
              <Select
                value={theme.font}
                onValueChange={(value) => set('font', value as ServicePdfTheme['font'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FONT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {theme.font === 'custom' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Regular font (TTF/OTF)">
                  <AssetPicker
                    accept=".ttf,.otf"
                    path={theme.font_regular_path}
                    busy={uploading === 'font_regular'}
                    onPick={(file) => upload('font_regular', file)}
                    onClear={() => set('font_regular_path', null)}
                  />
                </Field>
                <Field label="Bold font (TTF/OTF)">
                  <AssetPicker
                    accept=".ttf,.otf"
                    path={theme.font_bold_path}
                    busy={uploading === 'font_bold'}
                    onPick={(file) => upload('font_bold', file)}
                    onClear={() => set('font_bold_path', null)}
                  />
                </Field>
              </div>
            )}

            <div className="space-y-2">
              <Toggle
                label="Striped table rows"
                checked={theme.stripe_rows}
                onChange={(value) => set('stripe_rows', value)}
              />
              <Toggle
                label="Show gaps in the history"
                checked={theme.show_gaps}
                onChange={(value) => set('show_gaps', value)}
              />
              <Toggle
                label="Show parts that could not be read"
                checked={theme.show_unreadable}
                onChange={(value) => set('show_unreadable', value)}
              />
            </div>

            <Field label="Footer (every page)">
              <Input
                value={theme.footer_text}
                onChange={(e) => set('footer_text', e.target.value)}
                placeholder="Address · phone · website"
              />
            </Field>
            <Field label="Disclaimer (end of the document)">
              <textarea
                value={theme.disclaimer}
                onChange={(e) => set('disclaimer', e.target.value)}
                rows={2}
                className="border-input bg-background w-full rounded-md border p-2 text-sm"
              />
            </Field>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEdited(DEFAULT_SERVICE_PDF_THEME)}
                disabled={sameTheme(theme, DEFAULT_SERVICE_PDF_THEME)}
              >
                <RotateCcw className="mr-1 h-4 w-4" />
                Reset to default
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!changed || !!invalidMessage || updateSettings.isPending}
              >
                {updateSettings.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-1 h-4 w-4" />
                )}
                Save design
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">Preview with</span>
              <Select value={previewCar} onValueChange={setPreviewCar}>
                <SelectTrigger className="h-8 w-[220px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SAMPLE}>Sample car</SelectItem>
                  {(vehicles?.data ?? [])
                    .filter((vehicle) => vehicle.status === 'ready')
                    .map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.display_registration}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {previewing && <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />}
            </div>
            {invalidMessage && <p className="text-destructive text-xs">{invalidMessage}</p>}
            {previewError && <p className="text-destructive text-xs">{previewError}</p>}
            {previewUrl ? (
              <iframe
                src={previewUrl}
                title="PDF preview"
                className="h-[720px] w-full rounded-md border bg-white"
              />
            ) : (
              <div className="text-muted-foreground flex h-[720px] items-center justify-center rounded-md border text-sm">
                Building preview…
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block space-y-1">
    <span className="text-muted-foreground text-xs font-medium">{label}</span>
    {children}
  </label>
);

const Toggle = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) => (
  <div className="flex items-center justify-between rounded-md border px-3 py-2">
    <span className="text-sm">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
  </div>
);

/** Upload button for a logo or font; shows the stored file name once set. */
const AssetPicker = ({
  accept,
  path,
  busy,
  onPick,
  onClear,
}: {
  accept: string;
  path: string | null;
  busy: boolean;
  onPick: (file: File | undefined) => void;
  onClear: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
      >
        {busy ? (
          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
        ) : (
          <Upload className="mr-1 h-4 w-4" />
        )}
        {path ? 'Replace' : 'Upload'}
      </Button>
      {path && (
        <>
          <span className="text-muted-foreground truncate text-xs">Uploaded</span>
          <Button type="button" variant="ghost" size="sm" onClick={onClear} aria-label="Remove">
            <X className="h-4 w-4" />
          </Button>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(event) => {
          onPick(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </div>
  );
};
